/**
 * Per-skill RUN SPEC: MIRRORS apps/api/src/skills/skill-run-spec.ts.
 * Derived from a full read of all 59 seed SKILL.md manifests (Skills v2).
 *
 *   inputKind:  what the user must provide to run the skill meaningfully
 *     cv      : pick an existing CV
 *     cv_jd   : an existing CV + a job description (tailoring)
 *     job     : a job post / description (no CV picker)
 *     topic   : a short topic / subject (posts, carousels)
 *     text    : free-text describing the situation (coaching, interview, outreach)
 *     profile : nothing; builds from the user's stored profile
 *     none    : nothing
 *   outputKind: what the run returns
 *     cv_edit : writes/edits a CV (update-in-place or a named new version)
 *     text    : returns markdown text (copy / regenerate); NEVER creates a CV
 *
 * The importer stamps `inputKind`/`outputKind` onto each Skill row from this
 * map; the API + UI read them off the skill so the run flow is type-aware and
 * non-CV skills stop creating CV copies.
 */
export type SkillInputKind =
  | 'cv'
  | 'cv_jd'
  | 'job'
  | 'topic'
  | 'text'
  | 'profile'
  | 'none';
export type SkillOutputKind = 'cv_edit' | 'text';

export interface SkillRunSpec {
  inputKind: SkillInputKind;
  outputKind: SkillOutputKind;
}

export const SKILL_RUN_SPEC: Record<string, SkillRunSpec> = {
  'cv-wizard': { inputKind: 'profile', outputKind: 'cv_edit' },
  'cv-import': { inputKind: 'cv', outputKind: 'cv_edit' },
  'cv-rewrite': { inputKind: 'cv', outputKind: 'cv_edit' },
  'cv-ats': { inputKind: 'cv', outputKind: 'cv_edit' },
  'cv-style-switch': { inputKind: 'cv', outputKind: 'cv_edit' },
  'cv-for-people': { inputKind: 'cv', outputKind: 'cv_edit' },
  'cv-career-change': { inputKind: 'cv', outputKind: 'cv_edit' },
  'cv-academic': { inputKind: 'profile', outputKind: 'cv_edit' },
  'cv-student': { inputKind: 'profile', outputKind: 'cv_edit' },
  'cv-frontline': { inputKind: 'profile', outputKind: 'cv_edit' },
  'cv-healthcare': { inputKind: 'profile', outputKind: 'cv_edit' },
  'cv-tech': { inputKind: 'profile', outputKind: 'cv_edit' },
  'cv-returner': { inputKind: 'profile', outputKind: 'cv_edit' },
  'cv-multilingual': { inputKind: 'profile', outputKind: 'cv_edit' },
  'cv-uniform-to-civilian': { inputKind: 'profile', outputKind: 'cv_edit' },
  'cv-gcc-national': { inputKind: 'profile', outputKind: 'cv_edit' },
  'cv-expat-arab': { inputKind: 'profile', outputKind: 'cv_edit' },
  'cv-expat-fresh': { inputKind: 'profile', outputKind: 'cv_edit' },
  'cv-expat-mid': { inputKind: 'profile', outputKind: 'cv_edit' },
  'cv-expat-managerial': { inputKind: 'profile', outputKind: 'cv_edit' },
  'cv-expat-exec': { inputKind: 'profile', outputKind: 'cv_edit' },
  'cv-expat-southasian': { inputKind: 'profile', outputKind: 'cv_edit' },
  'cv-uae-fresh': { inputKind: 'profile', outputKind: 'cv_edit' },
  'cv-uae-mid': { inputKind: 'profile', outputKind: 'cv_edit' },
  'cv-uae-managerial': { inputKind: 'profile', outputKind: 'cv_edit' },
  'cv-uae-exec': { inputKind: 'profile', outputKind: 'cv_edit' },
  'cv-uae-gov': { inputKind: 'profile', outputKind: 'cv_edit' },
  'cv-audit': { inputKind: 'cv', outputKind: 'text' },
  'cv-privacy-audit': { inputKind: 'cv', outputKind: 'text' },
  'gap-analysis': { inputKind: 'cv', outputKind: 'text' },
  'cv-tailor': { inputKind: 'cv_jd', outputKind: 'cv_edit' },
  'cv-tailor-tech': { inputKind: 'cv_jd', outputKind: 'cv_edit' },
  'cv-tailor-gov': { inputKind: 'cv_jd', outputKind: 'cv_edit' },
  'cv-tailor-healthcare': { inputKind: 'cv_jd', outputKind: 'cv_edit' },
  'cv-tailor-internal': { inputKind: 'cv_jd', outputKind: 'cv_edit' },
  'cover-letter': { inputKind: 'cv_jd', outputKind: 'text' },
  'cover-letter-audit': { inputKind: 'text', outputKind: 'text' },
  'int-prep': { inputKind: 'text', outputKind: 'text' },
  'int-research': { inputKind: 'text', outputKind: 'text' },
  'int-questions': { inputKind: 'text', outputKind: 'text' },
  'int-stories': { inputKind: 'cv', outputKind: 'text' },
  'int-mock': { inputKind: 'text', outputKind: 'text' },
  'int-score': { inputKind: 'text', outputKind: 'text' },
  'int-negotiate': { inputKind: 'text', outputKind: 'text' },
  'int-followup': { inputKind: 'text', outputKind: 'text' },
  'li-audit': { inputKind: 'profile', outputKind: 'text' },
  'li-optimize': { inputKind: 'profile', outputKind: 'text' },
  'li-keyword': { inputKind: 'text', outputKind: 'text' },
  'brand-align': { inputKind: 'cv', outputKind: 'text' },
  'li-post': { inputKind: 'topic', outputKind: 'text' },
  'li-carousel': { inputKind: 'topic', outputKind: 'text' },
  'li-content-plan': { inputKind: 'text', outputKind: 'text' },
  'li-boolean': { inputKind: 'text', outputKind: 'text' },
  'linkedin-search': { inputKind: 'text', outputKind: 'text' },
  'job-from-post': { inputKind: 'job', outputKind: 'text' },
  'outreach-dm': { inputKind: 'text', outputKind: 'text' },
  'jobsearch-wellbeing': { inputKind: 'text', outputKind: 'text' },
  'career-coach': { inputKind: 'text', outputKind: 'text' },
  'portfolio-builder': { inputKind: 'text', outputKind: 'text' },
  // Current tabbio/v2 catalog. Skills that declare primary_cv use `cv` even
  // when their visible fields are text: the selected CV is reference context,
  // while the declarative inputs remain available in the run screen.
  'cv-health-check': { inputKind: 'cv', outputKind: 'text' },
  'follow-up-email': { inputKind: 'text', outputKind: 'text' },
  'hidden-jobs-search': { inputKind: 'cv', outputKind: 'text' },
  'interview-practice': { inputKind: 'cv', outputKind: 'text' },
  'linkedin-message': { inputKind: 'cv', outputKind: 'text' },
  'linkedin-optimizer': { inputKind: 'cv', outputKind: 'text' },
  'linkedin-post': { inputKind: 'cv', outputKind: 'text' },
  'salary-negotiation-gulf': { inputKind: 'text', outputKind: 'text' },
  'star-answer-builder': { inputKind: 'cv', outputKind: 'text' },
};

/** Manifest `run` block shape (mirrors skillRunConfigSchema in @tabbio/utils). */
export interface SkillRunConfigLike {
  input: 'cv' | 'text' | 'none';
  output: 'cv_edit' | 'markdown';
}

/**
 * Maps a manifest `run` block onto the runtime spec. Manifest `output:
 * markdown` is the run-spec's `text` kind; `input: cv|text|none` map 1:1.
 */
export function runSpecFromManifestRun(
  run: SkillRunConfigLike | null | undefined,
): SkillRunSpec | null {
  if (!run) return null;
  return {
    inputKind:
      run.input === 'cv' ? 'cv' : run.input === 'text' ? 'text' : 'none',
    outputKind: run.output === 'cv_edit' ? 'cv_edit' : 'text',
  };
}

/**
 * Canonical run-spec resolution order: MIRRORS the server copy exactly:
 *   `kind: cv_custom` ALWAYS forces cv→cv_edit (the server runs the CV path
 *   for cv_custom regardless of the `run` block, so a contradictory
 *   `run.output: markdown` must not advertise a text skill) → manifest `run`
 *   block → hardcoded seed map → category fallback.
 */
export function resolveRunSpec(
  manifest:
    | { run?: SkillRunConfigLike | null; kind?: 'default' | 'cv_custom' | null }
    | null
    | undefined,
  skillId: string,
  categories: string[] = [],
): SkillRunSpec {
  if (manifest?.kind === 'cv_custom') {
    return { inputKind: 'cv', outputKind: 'cv_edit' };
  }
  const fromManifest = runSpecFromManifestRun(manifest?.run);
  if (fromManifest) return fromManifest;
  return runSpecFor(skillId, categories);
}

/** Fallback when a skill isn't in the map: derive a safe default from categories. */
export function runSpecFor(
  skillId: string,
  categories: string[] = [],
): SkillRunSpec {
  const known = SKILL_RUN_SPEC[skillId];
  if (known) return known;
  const cat = categories[0] ?? '';
  if (
    [
      'social-post',
      'outreach',
      'job-search',
      'coaching',
      'wellbeing',
      'interview',
      'profile',
      'apply',
      'message',
      'general',
    ].includes(cat)
  ) {
    return { inputKind: 'text', outputKind: 'text' };
  }
  if (cat === 'cover-letter') return { inputKind: 'cv_jd', outputKind: 'text' };
  return { inputKind: 'cv', outputKind: 'cv_edit' };
}

/**
 * Whether a skill requires the CV picker (select an existing CV or import one
 * from the device). True when the skill writes to a CV (`cv_edit`) or reads a
 * stored CV as input (`cv` / `cv_jd`). Mirrors the `needsCvPicker` gate in
 * `SkillRunSheet`/`SkillRunDialog`; kept here as one pure, testable source so
 * the chat slash menu can decide whether to open the picker (issue #281).
 */
export function skillNeedsCvPicker(spec: SkillRunSpec): boolean {
  return (
    spec.outputKind === 'cv_edit' ||
    spec.inputKind === 'cv' ||
    spec.inputKind === 'cv_jd'
  );
}
